/* eslint-disable react-refresh/only-export-components */
import {
  faCircleExclamation,
  faCircleInfo,
  faTriangleExclamation,
} from "@fortawesome/pro-solid-svg-icons"
import { faXmark } from "@fortawesome/pro-regular-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { cva, type VariantProps } from "class-variance-authority"
import type { ComponentProps } from "react"
import { cn } from "cn"

const alertVariants = cva(
  "group/alert relative flex w-full items-start gap-3 rounded-xl border-2 border-solid bg-neutrals-v2-200 p-4 text-left [&_svg]:pointer-events-none [&_svg]:size-7 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        information: "border-indigo-100 [&>svg]:text-indigo-100",
        warning: "border-accent-orange-100 [&>svg]:text-accent-orange-100",
        error: "border-error-red-200 [&>svg]:text-error-red-200",
      },
    },
    defaultVariants: {
      variant: "information",
    },
  }
)

const alertIconByVariant = {
  information: faCircleInfo,
  warning: faTriangleExclamation,
  error: faCircleExclamation,
} as const

export type AlertVariant = NonNullable<
  VariantProps<typeof alertVariants>["variant"]
>

export type AlertProps = ComponentProps<"div"> &
  VariantProps<typeof alertVariants> & {
    title: string
    description?: string
    showIcon?: boolean
    showDescription?: boolean
    showCloseButton?: boolean
    /** Wird nur aufgerufen, wenn `showCloseButton` gesetzt ist. */
    onClose?: () => void
  }

function Alert({
  className,
  variant = "information",
  title,
  description = "Description",
  showIcon = true,
  showDescription = true,
  showCloseButton = false,
  onClose,
  ...props
}: AlertProps) {
  return (
    <div
      data-slot="alert"
      data-variant={variant}
      role="alert"
      className={cn(alertVariants({ variant, className }))}
      {...props}
    >
      {showIcon && (
        <FontAwesomeIcon
          data-slot="alert-icon"
          icon={alertIconByVariant[variant ?? "information"]}
        />
      )}
      {/* Figma-Text steht in Warning/Error auf „Brand Blue/100" (#0b1648) statt
          „Labels/Primary" (#000e46) wie bei Information — kaum sichtbarer
          Unterschied, hier auf das bestehende Token normalisiert. */}
      <div className="min-w-0 flex-1 text-labels-primary">
        <p className="text-sm leading-5 font-semibold">{title}</p>
        {showDescription && (
          <p className="text-xs leading-4 font-normal">{description}</p>
        )}
      </div>
      {showCloseButton && (
        <button
          type="button"
          data-slot="alert-close-button"
          onClick={onClose}
          className="flex size-6 shrink-0 items-center justify-center rounded p-0.5 text-labels-primary outline-none focus-visible:ring-2 focus-visible:ring-neutrals-v2-600 focus-visible:ring-offset-2"
        >
          <FontAwesomeIcon icon={faXmark} className="size-5!" />
          <span className="sr-only">Close</span>
        </button>
      )}
    </div>
  )
}

export { Alert, alertVariants }
